import { LayoutDashboard, ScrollText, Users2, Building2 } from 'lucide-react'
import { Link, NavLink, Outlet } from 'react-router-dom'

import { LogoMark } from '@/components/layout/Logo'
import { UserMenu } from '@/components/layout/UserMenu'
import { Badge, mutedTextClass } from '@/components/layout/ui'
import { cn } from '@/lib/cn'
import { useSession } from '@/lib/session'

/**
 * `/admin` — the shell around the platform console.
 *
 * It sits OUTSIDE `/:slug` on purpose. Everything else in the app is scoped to
 * one workspace and renders a sidebar of that workspace's projects; this
 * surface is the opposite of that — it reads across every tenant — so putting
 * it inside a workspace shell would have been a lie about what it is looking
 * at, and would have made "which workspace am I in" a meaningless question on
 * every screen.
 *
 * The banner is not decoration. Someone arrives here from the account menu,
 * often while helping a customer, and the difference between "my workspace" and
 * "everybody's data" needs to be legible without reading the URL.
 */
const NAV = [
  { to: '/admin', end: true, label: 'Overview', icon: LayoutDashboard },
  { to: '/admin/workspaces', end: false, label: 'Workspaces', icon: Building2 },
  { to: '/admin/users', end: false, label: 'Users', icon: Users2 },
  { to: '/admin/audit', end: false, label: 'Audit log', icon: ScrollText },
]

export default function AdminLayout() {
  const { defaultOrganization } = useSession()

  return (
    <div className="min-h-dvh bg-ink-50">
      <header className="border-b border-ink-200 bg-white">
        <div className="mx-auto flex h-14 max-w-6xl items-center justify-between gap-4 px-6">
          <div className="flex min-w-0 items-center gap-3">
            <Link
              to={defaultOrganization ? `/${defaultOrganization.slug}` : '/'}
              className="flex items-center gap-2.5 rounded-md"
              aria-label="Back to Taskly"
            >
              <LogoMark />
              <span className="text-sm font-semibold tracking-tight">Taskly</span>
            </Link>
            <span aria-hidden="true" className="text-ink-300">
              /
            </span>
            <span className="truncate text-sm font-medium">Platform admin</span>
            <Badge tone="warning">All workspaces</Badge>
          </div>
          <UserMenu />
        </div>

        <div className="mx-auto max-w-6xl px-6">
          <nav aria-label="Platform admin" className="-mb-px flex gap-1 overflow-x-auto">
            {NAV.map(({ to, end, label, icon: Icon }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                className={({ isActive }) =>
                  cn(
                    'inline-flex items-center gap-2 border-b-2 px-3 py-2.5 text-sm font-medium whitespace-nowrap transition',
                    isActive
                      ? 'border-brand-600 text-brand-700'
                      : 'border-transparent text-ink-600 hover:border-ink-300 hover:text-ink-900',
                  )
                }
              >
                <Icon className="size-4" aria-hidden="true" />
                {label}
              </NavLink>
            ))}
          </nav>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-8">
        <Outlet />
      </main>

      <footer className={cn('mx-auto max-w-6xl px-6 pb-10 text-xs', mutedTextClass)}>
        Every change made here is recorded in the audit log with your name and the reason you gave.
      </footer>
    </div>
  )
}
